import { debounce } from "perfect-debounce";

import {
  GraphNode,
  updateNodePositions as updateNodePositionsApi,
} from "../../api";

/**
 * Ensures position coordinates are integers for backend compatibility
 */
const ensureIntegerPosition = (
  position: GraphNode["position"],
): { x: number; y: number } | undefined => {
  if (!position) return undefined;
  return {
    x: Math.round(position.x ?? 0),
    y: Math.round(position.y ?? 0),
  };
};

/**
 * Debounce delay in ms - batches rapid position updates into a single API call
 */
const DEBOUNCE_DELAY = 50;

/**
 * Pending position updates, keyed by graphId
 */
const pendingUpdates = new Map<string, Map<string, GraphNode["position"]>>();

/**
 * Debounced flush functions, keyed by graphId
 */
const debouncedFlushFunctions = new Map<string, ReturnType<typeof debounce>>();

/**
 * Flush pending updates for a specific graph
 */
const flushPendingUpdates = async (graphId: string): Promise<void> => {
  const updates = pendingUpdates.get(graphId);
  if (!updates || updates.size === 0) return;

  const positions = Array.from(updates.entries())
    .map(([nodeId, position]) => {
      const integerPosition = ensureIntegerPosition(position);
      if (!integerPosition) return null;
      return { id: nodeId, position: integerPosition };
    })
    .filter(
      (p): p is { id: string; position: { x: number; y: number } } =>
        p !== null,
    );

  // Clear pending updates before API call
  updates.clear();

  if (positions.length === 0) return;

  void updateNodePositionsApi({
    body: {
      positions,
    },
    path: { id: graphId },
  });
};

/**
 * Clean up resources for a specific graphId.
 * Flushes any pending updates and removes entries from internal Maps.
 * Should be called when switching away from a graph to prevent memory leaks.
 */
export const cleanupGraphResources = async (graphId: string): Promise<void> => {
  // Flush any pending updates before cleaning up
  await flushPendingUpdates(graphId);

  // Cancel any pending debounced calls
  const debouncedFlush = debouncedFlushFunctions.get(graphId);
  if (debouncedFlush) {
    debouncedFlush.cancel?.();
  }

  // Remove entries from Maps
  pendingUpdates.delete(graphId);
  debouncedFlushFunctions.delete(graphId);
};

/**
 * Get or create a debounced flush function for a specific graph
 */
const getDebouncedFlush = (graphId: string): ReturnType<typeof debounce> => {
  if (!debouncedFlushFunctions.has(graphId)) {
    const debounced = debounce(
      () => flushPendingUpdates(graphId),
      DEBOUNCE_DELAY,
    );
    debouncedFlushFunctions.set(graphId, debounced);
  }
  return debouncedFlushFunctions.get(graphId)!;
};

/**
 * Update positions for multiple nodes in a single API call
 * Debounces rapid updates to batch them together
 */
export const updateNodePositions = (
  graphId: string,
  updates: Array<{ nodeId: string; position: GraphNode["position"] }>,
): void => {
  if (!graphId || updates.length === 0) return;

  // Get or create pending updates map for this graph
  if (!pendingUpdates.has(graphId)) {
    pendingUpdates.set(graphId, new Map());
  }
  const graphPendingUpdates = pendingUpdates.get(graphId)!;

  // Add new updates (merge with existing)
  for (const { nodeId, position } of updates) {
    graphPendingUpdates.set(nodeId, position);
  }

  // Call the debounced flush function
  const debouncedFlush = getDebouncedFlush(graphId);
  void debouncedFlush();
};

/**
 * Update position for a single node
 */
export const updateNodePosition = (
  graphId: string,
  nodeId: string,
  position: GraphNode["position"],
): void => {
  updateNodePositions(graphId, [{ nodeId, position }]);
};
